// @ts-nocheck
/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { PurpleSelectTrigger } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/PurpleSelectTrigger';
import { PurpleInput } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/PurpleInput';
import { StyledPurpleSelectWrapper } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/styles';
import { BasicFilterSelect } from 'ui/selects/FilterSelect';
import { BasicSelect } from 'ui/selects/BasicSelect';
import { pxToRem } from 'utils/convert';
import { Close16 } from 'ui/icons/action/Close16';
import { colors } from 'ui/theme/colors';
import type { BaseOption } from 'ui/actionMenu/types';
import {
  SEARCH_USER_FIELDS,
  PLACEHOLDER_SELECT_A_USER_FIELD,
  PLACEHOLDER_SELECT_A_VALUE,
  TEST_IDS,
} from 'modules/predictiveGoals/constants';
import { UpdatedOrConditionalBlock } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/LeadingOrSubsequentBlock';
import { isEmpty as isObjectEmpty } from '@iterable/utils';
import { getPropertyByNameFromProperties } from './PropertyBaseHelperFunctions';
import {
  QueryBaseContainer,
  QueryCloseButton,
  RightCol,
  RowFlexSpaceBetween,
  UpdatedInputGroup,
  UpdatedLeftCol,
  UpdatedMidCol,
} from '../Styled';
import {
  derivePropertyDescriptors,
  getNextPropertyComparison,
  getPropertyDefaultComparator,
} from '../utils/utils';
import {
  ComparatorOptionsWithNoValueRequirement,
  ComparatorOptionsWithNoSymbols,
} from '../constants';

export type OrQueryUpdate = {
  type: string;
  window: string;
  name: string;
  dataType: string;
  property_comparison?: {
    value: string;
    comparator: string;
  };
};

export const getNextPropertyUpdate = (
  { properties, newPropertyName },
  currentPropertyName,
  comparator,
  value,
) => {
  const filteredProperty = getPropertyByNameFromProperties(properties, newPropertyName);
  const defaultComparatorByProperty = getPropertyDefaultComparator(filteredProperty);
  let nextComparator = defaultComparatorByProperty;
  let nextValue;
  // Only preserve value and comparator if the property has not changed
  if ((comparator || value) && filteredProperty?.value === currentPropertyName) {
    nextComparator = comparator ?? defaultComparatorByProperty;
    nextValue = value;
  }
  return {
    filteredProperty,
    nextComparator,
    nextValue,
  };
};

export type PropertyBaseProps = {
  /** Default value for the comparator dropdown */
  comparator: string[];
  /** Options for the comparators dropdown */
  comparators: BaseOption[];
  /** Action fired when the delete icon is clicked */
  deleteAction: (e: React.MouseEvent) => void;
  /** Boolean to disable the comparators dropdown */
  disableComparators: boolean;
  /** Boolean to disable the values input field */
  disableValues: boolean;
  /** Identifier for the component */
  id: string;
  /** The string to be shown on left side of form fields */
  leftColText: string;
  /** Default value for the property dropdown */
  property: string;
  /** Options for the property dropdown */
  properties: BaseOption[];
  /** Setter for the comparator dropdown input change */
  setComparator: () => void;
  /** Setter for the value dropdown input change */
  setValue: () => void;
  /** Setter for the property dropdown input change */
  setProperty: () => void;
  /** Default value for the value dropdown */
  value: string[];
  /** Options for the values dropdown */
  values: BaseOption[];
};
export const PropertyBase = ({
  properties,
  onUpdate,
  onDelete,
  id,
  query,
  queryIdx,
}: PropertyBaseProps) => {
  const { type, name, dataType, window, property_comparison: propertyComparison } = query || {};
  const { comparator, value } = propertyComparison || {};
  const [selectedProperty, setSelectedProperty] = useState(
    getPropertyByNameFromProperties(properties, name),
  );
  const { values } = selectedProperty;
  const { filteredComparators, isLowCardinalityProperty, noSymbolIncluded } =
    derivePropertyDescriptors(query, selectedProperty);
  const [isNoSymbol, setIsNoSymbol] = useState(noSymbolIncluded);

  useEffect(() => {
    setIsNoSymbol(ComparatorOptionsWithNoSymbols.includes(comparator));
  }, [comparator]);

  const updateComparator = (newComparator: string, orQueryUpdate?: OrQueryUpdate) => {
    onUpdate({
      type: orQueryUpdate?.type || type,
      window: orQueryUpdate?.window || window,
      name: orQueryUpdate?.name || name,
      dataType: orQueryUpdate?.dataType || dataType,
      property_comparison: getNextPropertyComparison(
        orQueryUpdate?.property_comparison?.value || value,
        newComparator,
      ),
    });
  };

  const updateValue = (newValue, orQueryUpdate?: OrQueryUpdate) => {
    onUpdate({
      type: orQueryUpdate?.type || type,
      window: orQueryUpdate?.window || window,
      name: orQueryUpdate?.name || name,
      dataType: orQueryUpdate?.dataType || dataType,
      property_comparison: {
        value: newValue,
        comparator: orQueryUpdate?.property_comparison?.comparator || comparator,
      },
    });
  };

  const updateProperty = (propertyName: string) => {
    const { filteredProperty, nextComparator, nextValue } = getNextPropertyUpdate(
      { newPropertyName: propertyName, properties },
      name,
      comparator,
      value,
    );
    setSelectedProperty(filteredProperty);
    const orUpdate: OrQueryUpdate = {
      type,
      window,
      name: propertyName,
      dataType: filteredProperty?.dataType,
      property_comparison: {
        value: nextValue,
        comparator: nextComparator,
      },
    };
    // In the path that existing fields are updated, capture the new fields for the property
    onUpdate({
      ...orUpdate,
      property_comparison: {
        ...orUpdate?.property_comparison,
      },
    });

    // Ensure other fields are set on criteria update
    updateComparator(nextComparator, orUpdate);
    updateValue(nextValue, orUpdate);
  };

  useEffect(() => {
    if (!isObjectEmpty(selectedProperty) && !comparator) {
      updateComparator(getPropertyDefaultComparator(selectedProperty));
    }
  }, [name]);

  return (
    <QueryBaseContainer data-test={`${id}_PropertyBase`}>
      {
        <>
          <UpdatedLeftCol>
            <UpdatedOrConditionalBlock index={queryIdx} type="property" />
          </UpdatedLeftCol>
          <UpdatedMidCol>
            <RowFlexSpaceBetween>
              {
                <UpdatedInputGroup>
                  <BasicFilterSelect
                    width={`${pxToRem(400)}`}
                    defaultValues={[name]}
                    id={`${id}_PropertyBase`}
                    options={properties}
                    placeholder={`${PLACEHOLDER_SELECT_A_USER_FIELD}...`}
                    onSelect={([newProperty]) => updateProperty(newProperty)}
                    inputContent={{
                      filterPlaceholder: SEARCH_USER_FIELDS,
                    }}
                    SelectWrapper={StyledPurpleSelectWrapper}
                    Trigger={PurpleSelectTrigger}
                  />
                  {name && (
                    <BasicSelect
                      defaultValues={[comparator]}
                      id={`${id}_PropertyBase`}
                      options={filteredComparators}
                      onSelect={([newComparator]) =>
                        newComparator &&
                        newComparator !== comparator &&
                        updateComparator(newComparator)
                      }
                      isDisabled={!name}
                      suffixUsed={!isNoSymbol}
                      SelectWrapper={StyledPurpleSelectWrapper}
                      Trigger={PurpleSelectTrigger}
                      placeholder={PLACEHOLDER_SELECT_A_VALUE}
                    />
                  )}
                  {name &&
                    !ComparatorOptionsWithNoValueRequirement.includes(comparator) &&
                    (isLowCardinalityProperty ? (
                      <BasicSelect
                        defaultValues={[value]}
                        id={`${id}_PropertyBase`}
                        options={values}
                        onSelect={([newValue]) => updateValue(newValue)}
                        isDisabled={!values?.length}
                        SelectWrapper={StyledPurpleSelectWrapper}
                        Trigger={PurpleSelectTrigger}
                        placeholder={PLACEHOLDER_SELECT_A_VALUE}
                      />
                    ) : (
                      <PurpleInput
                        data-fullwidth={isEmpty(values)}
                        id={`${id}_PropertyBase`}
                        isRequired={true}
                        onChange={updateValue}
                        type="text"
                        value={value}
                      />
                    ))}
                </UpdatedInputGroup>
              }
            </RowFlexSpaceBetween>
          </UpdatedMidCol>
        </>
      }

      {
        <RightCol>
          <QueryCloseButton
            onClick={onDelete}
            data-test={TEST_IDS.DeletePropertyButton}
            color={colors.secondaryText}
          >
            <Close16 />
          </QueryCloseButton>
        </RightCol>
      }
    </QueryBaseContainer>
  );
};
